import React, { useState, useEffect } from 'react';
import { 
  Settings, User, Lock, Mail, Phone, Globe, Shield, 
  KeyRound, CheckCircle2, Eye, EyeOff, Save
} from 'lucide-react';
import { bdAPI } from '../utils/api';

const BDSettings = () => {
  const [profile, setProfile] = useState({
    user_id: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    country: '',
    agencies_count: 0
  });
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPass, setShowPass] = useState({ old: false, new: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await bdAPI.getProfile();
      if (res.data?.bd) {
        setProfile(res.data.bd);
      }
    } catch (err) {
      console.error("Error fetching BD profile:", err);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setFeedback({ type: '', message: '' });

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setFeedback({ type: 'error', message: 'New passwords do not match' });
      return;
    }

    setLoading(true);
    try {
      const res = await bdAPI.changePassword({
        oldPassword: passwordForm.oldPassword,
        newPassword: passwordForm.newPassword
      });
      if (res.data?.success) {
        setFeedback({ type: 'success', message: 'Password updated successfully!' });
        setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (err) {
      setFeedback({ type: 'error', message: err.response?.data?.message || 'Error updating password' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 max-w-4xl">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 text-emerald-400 text-xs font-bold uppercase tracking-wider mb-2">
          <Settings size={16} />
          <span>Profile & Security</span>
        </div>
        <h2 className="text-3xl font-black text-white tracking-tight">Account Settings</h2>
        <p className="text-slate-400 text-sm mt-1">
          Review your Business Development account details and manage credentials.
        </p>
      </div>

      {/* Profile Overview Card */}
      <div className="bg-[#0f172a] border border-white/10 rounded-[32px] p-8">
        <div className="flex items-center gap-5 mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-emerald-500 to-cyan-500 flex items-center justify-center text-white font-black text-2xl shadow-xl shadow-emerald-500/20">
            {profile.first_name ? profile.first_name.charAt(0).toUpperCase() : 'B'}
          </div>
          <div>
            <h3 className="text-2xl font-bold text-white">
              {profile.first_name} {profile.last_name}
            </h3>
            <div className="flex items-center gap-3 mt-1">
              <span className="font-mono text-xs font-bold text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-xl border border-emerald-500/20">
                {profile.user_id || 'BD Partner'}
              </span>
              <span className="px-2.5 py-1 rounded-xl bg-cyan-500/10 text-cyan-400 text-xs font-bold border border-cyan-500/20">
                Verified BD Executive
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5">
            <span className="text-xs font-bold text-slate-400 block mb-1">Email Address</span>
            <div className="flex items-center gap-2 text-white font-medium text-sm">
              <Mail size={16} className="text-emerald-400" />
              <span>{profile.email || 'Not specified'}</span>
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5">
            <span className="text-xs font-bold text-slate-400 block mb-1">Phone Number</span>
            <div className="flex items-center gap-2 text-white font-medium text-sm">
              <Phone size={16} className="text-emerald-400" />
              <span>{profile.phone || 'Not specified'}</span>
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5">
            <span className="text-xs font-bold text-slate-400 block mb-1">Country</span>
            <div className="flex items-center gap-2 text-white font-medium text-sm">
              <Globe size={16} className="text-emerald-400" />
              <span>{profile.country || 'India'}</span>
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5">
            <span className="text-xs font-bold text-slate-400 block mb-1">Assigned Agencies</span>
            <div className="flex items-center gap-2 text-white font-medium text-sm">
              <Shield size={16} className="text-emerald-400" />
              <span>{profile.agencies_count || 0} Managed Agencies</span>
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Card */}
      <div className="bg-[#0f172a] border border-white/10 rounded-[32px] p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
            <KeyRound size={22} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">Security & Password</h3>
            <p className="text-xs text-slate-400">Keep your BD portal credentials updated and secure</p>
          </div>
        </div>

        {feedback.message && (
          <div className={`mb-6 p-4 rounded-2xl text-xs font-bold ${
            feedback.type === 'success' 
              ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' 
              : 'bg-red-500/10 border border-red-500/20 text-red-400'
          }`}>
            {feedback.message}
          </div>
        )}

        <form onSubmit={handlePasswordSubmit} className="space-y-4 max-w-lg">
          <div>
            <label className="text-xs font-bold text-slate-400 block mb-1.5">Current Password</label>
            <div className="relative">
              <input
                type={showPass.old ? 'text' : 'password'}
                required
                value={passwordForm.oldPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-emerald-500 placeholder:text-slate-600"
                placeholder="Enter current password"
              />
              <button
                type="button"
                onClick={() => setShowPass({ ...showPass, old: !showPass.old })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
              >
                {showPass.old ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-400 block mb-1.5">New Password</label>
            <div className="relative">
              <input
                type={showPass.new ? 'text' : 'password'}
                required
                minLength={6}
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-emerald-500 placeholder:text-slate-600"
                placeholder="At least 6 characters"
              />
              <button
                type="button"
                onClick={() => setShowPass({ ...showPass, new: !showPass.new })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
              >
                {showPass.new ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-400 block mb-1.5">Confirm New Password</label>
            <div className="relative">
              <input
                type={showPass.confirm ? 'text' : 'password'}
                required
                minLength={6}
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-emerald-500 placeholder:text-slate-600"
                placeholder="Confirm new password"
              />
              <button
                type="button"
                onClick={() => setShowPass({ ...showPass, confirm: !showPass.confirm })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
              >
                {showPass.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-bold text-sm shadow-lg shadow-emerald-500/20 disabled:opacity-50 cursor-pointer mt-2"
          >
            {loading ? 'Updating Password...' : 'Save New Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default BDSettings;
